import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { Plus, MessageSquare, Send, ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

const CATEGORIES = ['Keamanan','Kebersihan','Infrastruktur','Sosial','Lingkungan','Lainnya'];
const STATUS_CFG = {
    open: { label: 'Terbuka', cls: 'badge-open' },
    in_progress: { label: 'Diproses', cls: 'badge-pending' },
    resolved: { label: 'Terselesaikan', cls: 'badge-resolved' },
    closed: { label: 'Ditutup', cls: 'badge-inactive' }
};
const CAT_COLORS = {
    Keamanan: 'bg-red-100 text-red-700', Kebersihan: 'bg-green-100 text-green-700',
    Infrastruktur: 'bg-blue-100 text-blue-700', Sosial: 'bg-purple-100 text-purple-700',
    Lingkungan: 'bg-teal-100 text-teal-700', Lainnya: 'bg-slate-100 text-slate-600'
};

const emptyForm = { title: '', description: '', category: 'Lainnya', submitter_name: '', submitter_phone: '' };

export default function ComplaintPage() {
    const { user } = useAuth();
    const { t } = useLang();
    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [comment, setComment] = useState('');
    const [commenting, setCommenting] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/complaints`, { withCredentials: true });
            setComplaints(res.data);
        } catch { toast.error('Gagal memuat data pengaduan'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const handleSubmit = async () => {
        if (!form.title || !form.description || !form.submitter_name) {
            toast.error('Isi judul, deskripsi, dan nama'); return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/complaints`, form, { withCredentials: true });
            toast.success('Pengaduan berhasil dikirim');
            setShowForm(false); setForm(emptyForm); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mengirim'); }
        setSaving(false);
    };

    const updateStatus = async (id, status) => {
        try {
            await axios.put(`${API}/complaints/${id}/status`, { status }, { withCredentials: true });
            toast.success('Status diperbarui'); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    };

    const addComment = async (id) => {
        if (!comment.trim()) return;
        setCommenting(true);
        try {
            await axios.post(`${API}/complaints/${id}/comments`, { text: comment }, { withCredentials: true });
            setComment(''); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
        setCommenting(false);
    };

    const filtered = filterStatus === 'all' ? complaints : complaints.filter(c => c.status === filterStatus);
    const openCount = complaints.filter(c => c.status === 'open').length;

    return (
        <div className="space-y-5" data-testid="complaints-page">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="page-header text-2xl">{t.complaintsTitle}</h1>
                    <p className="text-sm text-slate-500 mt-1">{openCount} pengaduan terbuka dari {complaints.length} total</p>
                </div>
                <Button onClick={() => setShowForm(true)} className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-complaint-btn">
                    <Plus className="w-4 h-4" /> {t.addComplaint}
                </Button>
            </div>

            {/* Filter */}
            <div className="flex gap-2 flex-wrap">
                {[['all', 'Semua'], ['open', 'Terbuka'], ['in_progress', 'Diproses'], ['resolved', 'Terselesaikan']].map(([val, label]) => (
                    <button key={val} onClick={() => setFilterStatus(val)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            filterStatus === val ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`} data-testid={`filter-${val}`}>
                        {label}
                    </button>
                ))}
            </div>

            {/* Complaints list */}
            {loading ? (
                <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-xl" />)}</div>
            ) : filtered.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
                    <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400 font-medium">Tidak ada pengaduan</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map(c => {
                        const cfg = STATUS_CFG[c.status] || STATUS_CFG.open;
                        const isExp = expanded === c.id;
                        return (
                            <div key={c.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
                                <div className="p-5">
                                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-2">
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CAT_COLORS[c.category] || 'bg-slate-100 text-slate-600'}`}>
                                                    {c.category}
                                                </span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}>{cfg.label}</span>
                                            </div>
                                            <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{c.title}</h3>
                                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{c.description}</p>
                                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
                                                <span>Pelapor: <strong className="text-slate-600">{c.submitter_name}</strong></span>
                                                {c.submitter_phone && <span>HP: {c.submitter_phone}</span>}
                                                <span>{new Date(c.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                                <span>{c.comments?.length || 0} komentar</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {/* Status change */}
                                            <Select value={c.status} onValueChange={v => updateStatus(c.id, v)}>
                                                <SelectTrigger className="h-8 text-xs w-36" data-testid={`status-${c.id}`}><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="open">Terbuka</SelectItem>
                                                    <SelectItem value="in_progress">Diproses</SelectItem>
                                                    <SelectItem value="resolved">Terselesaikan</SelectItem>
                                                    <SelectItem value="closed">Ditutup</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <button onClick={() => setExpanded(isExp ? null : c.id)}
                                                className="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                                                data-testid={`expand-complaint-${c.id}`}>
                                                {isExp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded: comments */}
                                {isExp && (
                                    <div className="border-t border-slate-100 bg-slate-50 p-4 space-y-3">
                                        {c.comments?.length > 0 ? (
                                            <div className="space-y-2">
                                                {c.comments.map((cm, i) => (
                                                    <div key={i} className="flex gap-3">
                                                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">
                                                            {cm.author?.charAt(0)?.toUpperCase()}
                                                        </div>
                                                        <div className="flex-1 bg-white rounded-lg px-3 py-2 border border-slate-200">
                                                            <p className="text-xs font-semibold text-slate-600">{cm.author}</p>
                                                            <p className="text-sm text-slate-700 mt-0.5">{cm.text}</p>
                                                            <p className="text-xs text-slate-400 mt-1">{new Date(cm.created_at).toLocaleString('id-ID')}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-slate-400 text-center py-2">Belum ada komentar</p>
                                        )}
                                        {/* Add comment */}
                                        <div className="flex gap-2">
                                            <Input value={comment} onChange={e => setComment(e.target.value)}
                                                placeholder="Tulis komentar atau tanggapan..." className="text-sm h-9 bg-white"
                                                onKeyDown={e => e.key === 'Enter' && addComment(c.id)}
                                                data-testid={`comment-input-${c.id}`} />
                                            <Button onClick={() => addComment(c.id)} disabled={commenting} size="sm" className="bg-primary hover:bg-primary/90 h-9 px-3"
                                                data-testid={`send-comment-${c.id}`}>
                                                {commenting ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* New complaint dialog */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Buat Pengaduan Baru</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>{t.complaintTitleField} *</Label>
                            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="Ringkasan masalah..." data-testid="complaint-title-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.category}</Label>
                            <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                                <SelectTrigger data-testid="complaint-cat-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.description} *</Label>
                            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="Jelaskan masalah secara detail..." rows={3} data-testid="complaint-desc-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>{t.submitterName} *</Label>
                                <Input value={form.submitter_name} onChange={e => setForm(f => ({ ...f, submitter_name: e.target.value }))}
                                    data-testid="complaint-name-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t.submitterPhone}</Label>
                                <Input value={form.submitter_phone} onChange={e => setForm(f => ({ ...f, submitter_phone: e.target.value }))} />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForm(false)}>{t.cancel}</Button>
                        <Button onClick={handleSubmit} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="submit-complaint-btn">
                            {saving ? 'Mengirim...' : 'Kirim Pengaduan'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
